// src/components/analytics/predictors/ARIMAPredictor.js

import { BasePredictor } from './base/BasePredictor';
import { 
  parseDate, 
  formatDate, 
  dateToNumeric, 
  numericToDate, 
  analyzeDateFrequency,
  generateFutureDatesWithFrequency,
  detectDateFormat,
  getDateDifference
} from '../utils/dateUtils';
import { isValid } from 'date-fns';

export class ARIMAPredictor extends BasePredictor {
  constructor(config) {
    super(config);
    this.method = 'arima';
    
    // === AUTO-TUNE p,d,q (and seasonal) ===
    this.seasonalPeriod = config.seasonalPeriod || null;

    // ARIMA parameters - allow 'auto' for automatic selection
    this.p = config.p !== undefined ? config.p : 'auto';
    this.d = config.d !== undefined ? config.d : 'auto';
    this.q = config.q !== undefined ? config.q : 'auto';

    // Use defaults if not auto
    if (this.p === 'auto') this.p = 1;
    if (this.d === 'auto') this.d = 1;
    if (this.q === 'auto') this.q = 1;
    
    // Debug log to verify seasonal parameters
    console.log('ARIMA constructor - seasonal parameters:', {
      seasonalStrength: config.seasonalStrength,
      minSeasonalWeight: config.minSeasonalWeight,
      volatilityFactor: config.volatilityFactor,
      seasonalDecay: config.seasonalDecay,
      seasonalPeriod: this.seasonalPeriod,
      p: this.p,
      d: this.d,
      q: this.q
    });
  }

  /**
   * Check if a time series is stationary
   * @param {Array} values - Array of numeric values
   * @returns {boolean} - True if stationary
   */
  isStationary(values) {
    if (values.length < 10) return false; // Need enough data
    
    // Simple trend test - compare first and second half means
    const n = values.length;
    const firstHalf = values.slice(0, Math.floor(n/2));
    const secondHalf = values.slice(Math.floor(n/2));
    
    const mean1 = firstHalf.reduce((a,b) => a+b, 0) / firstHalf.length;
    const mean2 = secondHalf.reduce((a,b) => a+b, 0) / secondHalf.length;
    
    // Check if means are relatively similar (within 10%)
    const meanDiff = Math.abs(mean2 - mean1);
    const avgMean = (Math.abs(mean1) + Math.abs(mean2)) / 2;
    
    // Also check variance stability
    const var1 = firstHalf.reduce((a,b) => a + Math.pow(b - mean1, 2), 0) / firstHalf.length;
    const var2 = secondHalf.reduce((a,b) => a + Math.pow(b - mean2, 2), 0) / secondHalf.length;
    const varRatio = Math.max(var1, var2) / Math.max(Math.min(var1, var2), 0.001);
    
    return meanDiff < (avgMean * 0.1) && varRatio < 2;
  }

  /**
   * Calculate differences for stationarity
   * @param {Array} values - Array of numeric values
   * @param {number} order - Differencing order
   * @returns {Array} - Differenced values
   */
  difference(values, order = 1) {
    let result = [...values];
    
    for (let i = 0; i < order; i++) {
      const temp = [];
      for (let j = 1; j < result.length; j++) {
        temp.push(result[j] - result[j - 1]);
      }
      result = temp;
    }
    
    return result;
  }

  /**
   * Reverse differencing to get original scale
   * @param {Array} diffValues - Differenced values
   * @param {Array} originalValues - Original values for base
   * @param {number} order - Differencing order
   * @returns {Array} - Values in original scale
   */
  inverseDifference(diffValues, originalValues, order = 1) {
    let result = [...diffValues];
    
    for (let i = 0; i < order; i++) {
      const temp = [originalValues[originalValues.length - order + i]];
      
      for (let j = 0; j < result.length; j++) {
        temp.push(temp[temp.length - 1] + result[j]);
      }
      
      result = temp.slice(1);
    }
    
    return result;
  }

  /**
   * Simple AR(p) coefficient estimation using least squares
   * @param {Array} values - Time series values
   * @param {number} p - AR order
   * @returns {Array} - AR coefficients
   */
  estimateARCoefficients(values, p) {
    if (values.length <= p) {
      throw new Error('Insufficient data for AR estimation');
    }

    // Create design matrix and response vector
    const X = [];
    const y = [];
    
    for (let i = p; i < values.length; i++) {
      const row = [];
      for (let j = 1; j <= p; j++) {
        row.push(values[i - j]);
      }
      X.push(row);
      y.push(values[i]);
    }

    // Simple least squares solution
    const coefficients = this.leastSquares(X, y);
    
    return coefficients;
  }

  /**
   * Simple least squares solver
   * @param {Array} X - Design matrix
   * @param {Array} y - Response vector
   * @returns {Array} - Coefficients
   */
  leastSquares(X, y) {
    const n = X.length;
    const p = X[0].length;
    
    // For simplicity, implementing for p=1 case
    if (p === 1) {
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      
      for (let i = 0; i < n; i++) {
        sumX += X[i][0];
        sumY += y[i];
        sumXY += X[i][0] * y[i];
        sumX2 += X[i][0] * X[i][0];
      }
      
      const coef = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      return [coef];
    }
    
    // For higher orders, use declining weights instead of equal weights
    const coefficients = [];
    for (let j = 0; j < p; j++) {
      // Weights decline linearly: p/(p+1)/2, (p-1)/(p+1)/2, ..., 1/(p+1)/2
      coefficients.push((p - j) / ((p * (p + 1)) / 2));
    }
    
    return coefficients;
  }

  /**
   * Generate ARIMA forecasts with MA component
   * @param {Array} values - Historical values
   * @param {number} steps - Number of steps to forecast
   * @returns {Object} - { forecasts: Array, errors: Array }
   */
  forecast(values, steps) {
    // Check stationarity and apply differencing if needed
    let diffValues;
    let actualDifferencingOrder;

    /* Determine whether we really need to difference.
      One simple rule-of-thumb: if the series already looks stationary,
      don't difference – it only injects a downward drift. */
    const needsDiff = !this.isStationary(values); // auto-detect
    actualDifferencingOrder = needsDiff ? 1 : 0;
    diffValues = needsDiff 
      ? this.difference(values, 1) // one-step diff is plenty
      : [...values]; // keep original scale
    
    if (diffValues.length < this.p) {
      throw new Error('Insufficient data after differencing');
    }

    // Estimate AR coefficients
    const arCoefficients = this.estimateARCoefficients(diffValues, this.p);
    
    // Initialize arrays for forecasting
    const forecasts = [];
    const errors = [];
    const extendedValues = [...diffValues];

    // Calculate mean of differenced values for mean reversion
    const mean = diffValues.reduce((a, b) => a + b, 0) / diffValues.length;

    // Detect seasonal pattern for use in forecasting
    const seasonalPeriod = this.detectSeasonality(diffValues);
    const hasSeasonality = seasonalPeriod > 0;
    console.log(`ARIMA: Seasonal period detected: ${seasonalPeriod} points`);

    // Calculate initial errors for MA component
    for (let i = this.p; i < diffValues.length; i++) {
      let arPred = 0;
      for (let j = 0; j < this.p; j++) {
        arPred += arCoefficients[j] * diffValues[i - j - 1];
      }
      errors.push(diffValues[i] - arPred);
    }
    
    // Generate forecasts
    for (let h = 0; h < steps; h++) {
      let forecast = 0;
      
      // AR component - use the most recent values
      for (let i = 0; i < this.p; i++) {
        const idx = extendedValues.length - i - 1;
        if (idx >= 0 && idx < extendedValues.length) {
          forecast += arCoefficients[i] * extendedValues[idx];
        }
      }
      
      // Calculate recent trend more accurately
      const recentWindow = Math.min(12, Math.floor(diffValues.length / 3));
      const recentValues = diffValues.slice(-recentWindow);
      const recentMean = recentValues.reduce((a, b) => a + b, 0) / recentValues.length;

      // Calculate trend strength (needed for dampening factor later)
      const trendStrength = Math.abs(recentMean) / (Math.abs(mean) + 0.001);

      // Apply seasonal pattern if detected
      if (hasSeasonality) {
        // Configuration for seasonal strength (make this configurable)
        const seasonalConfig = {
          initialWeight: this.config.seasonalStrength || 0.7,  // Default 70%
          minWeight: this.config.minSeasonalWeight || 0.3,    // Default 30%
          decayRate: this.config.seasonalDecay || 0.015,      // How fast seasonal influence decays
          volatilityFactor: this.config.volatilityFactor || 0.3  // How much random variation
        };
        
        // Look back to same position in previous cycles
        const positionInCycle = h % seasonalPeriod;
        const historicalPositions = [];
        const historicalDiffs = [];
        
        // Collect multiple cycles (generic approach)
        const maxCycles = Math.min(4, Math.floor(diffValues.length / seasonalPeriod));
        for (let cycle = 1; cycle <= maxCycles; cycle++) {
          const lookbackIndex = diffValues.length - (cycle * seasonalPeriod) + positionInCycle;
          if (lookbackIndex >= 0 && lookbackIndex < diffValues.length) {
            historicalPositions.push(diffValues[lookbackIndex]);
            
            if (lookbackIndex >= seasonalPeriod) {
              historicalDiffs.push(diffValues[lookbackIndex] - diffValues[lookbackIndex - seasonalPeriod]);
            }
          }
        }
        
        if (historicalPositions.length > 0) {
          // Calculate seasonal component
          const seasonalBase = historicalPositions.reduce((a, b) => a + b, 0) / historicalPositions.length;
          const typicalChange = historicalDiffs.length > 0 ?
            historicalDiffs.reduce((a, b) => a + b, 0) / historicalDiffs.length : 0;
          
          // Adaptive seasonal weight based on configuration
          const seasonalWeight = Math.max(
            seasonalConfig.minWeight, 
            seasonalConfig.initialWeight - h * seasonalConfig.decayRate
          );
          
          // Blend AR and seasonal components
          forecast = forecast * (1 - seasonalWeight) + 
                    (seasonalBase + typicalChange * Math.floor(h / seasonalPeriod)) * seasonalWeight;
          
          // Add controlled volatility
          if (historicalPositions.length > 1) {
            const variance = historicalPositions.reduce((sum, val) => 
              sum + Math.pow(val - seasonalBase, 2), 0) / historicalPositions.length;
            const stdDev = Math.sqrt(variance);
            forecast += (Math.random() - 0.5) * stdDev * seasonalConfig.volatilityFactor;
          }
          
          console.log(`Seasonal adjustment: position=${positionInCycle}, weight=${seasonalWeight.toFixed(2)}`);
        }
      } else {
        // Original trend-based logic when no seasonality
        if (trendStrength > 1.2) {
          forecast = forecast * 0.95 + recentMean * 0.05;
        } else {
          forecast = forecast * 0.85 + mean * 0.15;
        }

        // Add volatility preservation for highly variable data
        const volatility = Math.sqrt(diffValues.reduce((sum, val) => 
          sum + Math.pow(val - mean, 2), 0) / diffValues.length);
        const coefficientOfVariation = volatility / (Math.abs(mean) + 0.001);

        // If data is highly volatile (CV > 0.5), add random variation
        if (coefficientOfVariation > 0.5) {
          const volatilityComponent = (Math.random() - 0.5) * volatility * 0.5;
          forecast += volatilityComponent;
          console.log(`Adding volatility component: CV=${coefficientOfVariation.toFixed(2)}, component=${volatilityComponent.toFixed(0)}`);
        }
      }
      
      // MA component with random walk for variation
      if (this.q > 0 && errors.length >= this.q) {
        let maComponent = 0;
        const recentErrors = errors.slice(-this.q);
        
        for (let i = 0; i < this.q && i < recentErrors.length; i++) {
          const weight = (this.q - i) / ((this.q * (this.q + 1)) / 2);
          maComponent += weight * recentErrors[recentErrors.length - 1 - i];
        }
        
        // Add small random variation to prevent identical forecasts
        const variation = (Math.random() - 0.5) * 0.1 * Math.abs(mean);
        forecast += maComponent * 0.3 + variation;
      }
      
      // Trend component with dampening
      if (extendedValues.length > 5) {
        const windowSize = Math.min(7, Math.floor(extendedValues.length / 2));
        const recentWindow = extendedValues.slice(-windowSize);
        const olderWindow = extendedValues.slice(-2 * windowSize, -windowSize);
        
        if (olderWindow.length > 0) {
          const recentMean = recentWindow.reduce((a, b) => a + b, 0) / recentWindow.length;
          const olderMean = olderWindow.reduce((a, b) => a + b, 0) / olderWindow.length;
          const trend = (recentMean - olderMean) / windowSize;
          
          // Apply trend with less aggressive dampening for strong trends
          // But reduce trend influence when seasonality is detected
          const seasonalReduction = hasSeasonality ? 0.5 : 1.0;
          const dampingFactor = trendStrength > 1.2 ? 
            Math.exp(-h * 0.005) :  // Slower dampening
            Math.exp(-h * 0.01);    // Less aggressive dampening
          forecast += trend * dampingFactor * seasonalReduction;
        }
      }
      
      extendedValues.push(forecast);
      forecasts.push(forecast);
      
      // Update errors for next iteration
      const estimatedError = h === 0 ? 0 : 
        (errors.length > 0 ? errors[errors.length - 1] * Math.exp(-h * 0.1) : 0);
      errors.push(estimatedError);
    }
    
    // Inverse difference to get values in original scale
    let originalForecasts;
    if (actualDifferencingOrder > 0) {
      originalForecasts = this.inverseDifference(forecasts, values, actualDifferencingOrder);
    } else {
      originalForecasts = forecasts;
    }
    
    // Ensure positive forecasts if historical data is all positive
    originalForecasts = this.ensurePositiveForecasts(originalForecasts, values);
    
    return { forecasts: originalForecasts, forecastErrors: errors };
  }

  /**
   * Calculate prediction intervals with growing uncertainty
   * @param {Array} values - Historical values
   * @param {Array} forecasts - Forecasted values
   * @param {number} confidenceLevel - Confidence level
   * @returns {Array} - Array of {lower, upper} bounds
   */
  calculatePredictionIntervals(values, forecasts, confidenceLevel = 0.95) {
    // Calculate residual standard deviation from recent data
    const residuals = [];
    const windowSize = Math.min(values.length - 1, 20); // Use last 20 points max
    
    for (let i = values.length - windowSize; i < values.length - 1; i++) {
      if (i >= 0) {
        residuals.push(values[i + 1] - values[i]);
      }
    }
    
    if (residuals.length === 0) {
      // Fallback if not enough data
      const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
      const stdDev = avgValue * 0.1; // 10% of average as fallback
      return forecasts.map((forecast, h) => {
        const uncertainty = stdDev * Math.sqrt(1 + h + h * h * 0.01);
        return { lower: forecast - uncertainty, upper: forecast + uncertainty };
      });
    }
    
    const mean = residuals.reduce((a, b) => a + b, 0) / residuals.length;
    const variance = residuals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / residuals.length;
    const stdDev = Math.sqrt(variance);
    
    // Z-score for confidence level
    const zScore = confidenceLevel === 0.95 ? 1.96 : 2.58;
    
    // Calculate intervals with properly growing uncertainty
    return forecasts.map((forecast, h) => {
      // Uncertainty grows with forecast horizon
      // Includes linear and quadratic components for realistic growth
      const horizonFactor = Math.sqrt(1 + h + h * h * 0.01);
      const uncertainty = stdDev * horizonFactor * zScore;
      
      return {
        lower: forecast - uncertainty,
        upper: forecast + uncertainty
      };
    });
  }

  /**
   * Main prediction method
   * @param {Array} data - The data to analyze
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Object} - { predictions: Array, metadata: Object }
   */
  predict(data, xAxis, yAxis) {
    // Validate data
    const validation = this.validateData(data, xAxis, yAxis);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Safety check for prediction limit
    const maxAllowedPredictions = 365;
    if (this.config.numPredictions && this.config.numPredictions > maxAllowedPredictions) {
      console.warn(`Limiting predictions from ${this.config.numPredictions} to ${maxAllowedPredictions}`);
      this.config.numPredictions = maxAllowedPredictions;
    }

    // Parse resolution from config or user query
    this.detectResolution();

    // Extract time series with proper aggregation
    const timeSeries = this.extractTimeSeries(
      data, 
      xAxis, 
      yAxis, 
      this.config.aggregationMethod || 'sum'
    );

    // After aggregation, re-analyze the dates for proper frequency detection
    console.log(`ARIMA: Extracted ${timeSeries.length} unique time points after aggregation`);

    // For highly volatile monthly data, recommend more points
    const minPointsNeeded = Math.max(12, this.p + this.d + this.q + 5);
    if (timeSeries.length < minPointsNeeded) {
      console.warn(`Limited data points (${timeSeries.length}) for ARIMA(${this.p},${this.d},${this.q}). Recommended: ${minPointsNeeded} points. Forecast quality may be reduced.`);
      // Don't throw error, but adjust parameters
      if (timeSeries.length < 10) {
        this.d = 0; // Skip differencing for very small datasets
        console.log('Disabling differencing due to limited data points');
      }
    }

    // Check if x-axis is dates
    const firstX = timeSeries[0].x;
    const isDateAxis = this.isDateValue(firstX);
    
    if (!isDateAxis) {
      throw new Error('ARIMA requires time series data with date/time x-axis');
    }

    // Store original date format from the first data point
    let originalDateFormat = null;
    if (timeSeries.length > 0) {
      originalDateFormat = timeSeries[0].x;
    }

    // Parse dates and prepare data
    const dates = [];
    const values = [];

    for (const point of timeSeries) {
      const parsedDate = parseDate(point.x);
      if (parsedDate) {
        dates.push(parsedDate);
        values.push(point.y);
      }
    }

    // ---------- one-shot PDQ auto-tune ----------
    if (this.p === 'auto' || this.d === 'auto' || this.q === 'auto') {
      this.autoSelectPDQ(values);
    }

    // Detect date format and frequency
    const dateFormat = detectDateFormat(timeSeries[0].x);
    let frequency, averageInterval;

    // If data was aggregated, use that resolution
    if (timeSeries[0].aggregationType) {
      frequency = timeSeries[0].aggregationType;
      switch (frequency) {
        case 'weekly':
          averageInterval = 7;
          break;
        case 'monthly':
          averageInterval = 30;
          break;
        case 'yearly':
          averageInterval = 365;
          break;
      }
      console.log(`ARIMA: Using aggregated ${frequency} data with interval ${averageInterval} days`);
    } else {
      // Detect frequency from the data
      const frequencyAnalysis = analyzeDateFrequency(dates);
      frequency = frequencyAnalysis.frequency;
      averageInterval = frequencyAnalysis.averageInterval;
      
      // Override frequency detection if it seems wrong
      if (frequency === 'daily' && averageInterval >= 5 && averageInterval <= 9) {
        frequency = 'weekly';
        averageInterval = 7;
        console.log('ARIMA: Overriding frequency detection from daily to weekly');
      }
    }

    console.log(`ARIMA: Detected frequency: ${frequency}, average interval: ${averageInterval} days`);

    // Generate future dates
    const lastDate = dates[dates.length - 1];
    const predictUntil = this.config.predictUntil || null;
    
    // Use the shared method from BasePredictor to calculate predictions needed
    const numPredictions = this.calculatePredictionsNeeded(
      lastDate,
      predictUntil,
      frequency,
      averageInterval
    );

    const futureDates = generateFutureDatesWithFrequency(
      lastDate,
      predictUntil,
      frequency,
      averageInterval
    );

    // Limit to calculated number of predictions
    const limitedDates = futureDates.slice(0, numPredictions);

    // Generate ARIMA forecasts
    let forecastResult;
    let intervals;
    
    try {
      forecastResult = this.forecast(values, numPredictions);
      
      if (this.config.includeConfidenceInterval) {
        intervals = this.calculatePredictionIntervals(
          values, 
          forecastResult.forecasts, 
          this.config.confidenceLevel || 0.95
        );
      }
    } catch (error) {
      console.error('ARIMA forecasting error:', error);
      throw new Error(`ARIMA forecasting failed: ${error.message}`);
    }

    // Format predictions
    const predictions = limitedDates.map((date, index) => {
      const prediction = {
        x: originalDateFormat ? formatDate(date, originalDateFormat) : formatDate(date, dateFormat || 'yyyy-MM-dd'),
        y: forecastResult.forecasts[index],
        _futureIndex: index,
        _isPrediction: true
      };

      if (intervals && intervals[index]) {
        prediction.confidenceLower = intervals[index].lower;
        prediction.confidenceUpper = intervals[index].upper;
      }

      return prediction;
    });

    // Format output
    const formattedPredictions = this.formatPredictionOutput(predictions, xAxis, yAxis, data[0]);

    return {
      predictions: formattedPredictions,
      metadata: {
        method: this.method,
        arimaOrder: {
          p: this.p,
          d: this.d,
          q: this.q
        },
        dataPoints: timeSeries.length,
        isTimeSeries: true,
        frequency: frequency,
        averageInterval: averageInterval,
        confidenceLevel: this.config.confidenceLevel || 0.95,
        wasStationary: this.isStationary(values),
        config: this.config
      }
    };
  }

  /**
   * Check if a value represents a date
   * @param {any} value - Value to check
   * @returns {boolean}
   */
  isDateValue(value) {
    if (!value) return false;
    
    const stringValue = String(value);
    
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/,     // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}/,   // DD/MM/YYYY or MM/DD/YYYY
      /^\d{4}\/\d{2}\/\d{2}/,   // YYYY/MM/DD
      /^\d{4}-\d{2}$/,          // YYYY-MM
      /^[A-Za-z]+ \d{4}$/       // Month YYYY
    ];

    return datePatterns.some(pattern => pattern.test(stringValue));
  }

  /**
   * Ensure forecasts remain positive for positive-only series
   * @param {Array} forecasts - Raw forecasts
   * @param {Array} historicalValues - Historical values to check if all positive
   * @returns {Array} - Adjusted forecasts
   */
  ensurePositiveForecasts(forecasts, historicalValues) {
    // Check if all historical values are positive
    const allPositive = historicalValues.every(val => val > 0);
    
    if (!allPositive) {
      return forecasts; // Don't constrain if historical data has non-positive values
    }

    // Find minimum historical value for floor reference
    const minHistorical = Math.min(...historicalValues);
    const minThreshold = minHistorical * 0.1; // Use 10% of minimum as floor

    // Adjust any negative or very small forecasts
    return forecasts.map(forecast => {
      if (forecast <= 0) {
        // Use exponential decay from last positive historical value
        const lastPositive = historicalValues[historicalValues.length - 1];
        return Math.max(minThreshold, lastPositive * 0.5);
      }
      return Math.max(forecast, minThreshold);
    });
  }

  /**
   * Detect basic seasonality in the data
   * @param {Array} values - Time series values
   * @returns {number} - Detected seasonal period (0 if none)
   */
  detectSeasonality(values) {
    // If backend already detected seasonality, trust it
    if (this.seasonalPeriod && this.seasonalPeriod > 0) {
      console.log(`Using seasonal period ${this.seasonalPeriod} from backend analysis`);
      return this.seasonalPeriod;
    }
    
    // Otherwise, detect it ourselves
    if (values.length < 14) return 0; // Need at least 2 weeks of data
    
    // Try more periods including 4-5 week patterns
    const periods = [4, 5, 8, 13, 26, 52]; // Added 5 weeks and 8 weeks (2 months)
    let bestPeriod = 0;
    let bestScore = -Infinity;
    
    console.log(`Detecting seasonality in ${values.length} data points`);
    
    // Also check for cyclic patterns using variance analysis
    const cyclicScores = new Map();
    
    for (const period of periods) {
      if (values.length < period * 2) continue; // Need at least 2 full periods
      
      // Calculate autocorrelation for this lag
      const n = values.length;
      const mean = values.reduce((a, b) => a + b, 0) / n;
      
      let numerator = 0;
      let denominator = 0;
      let count = 0;
      
      for (let i = period; i < n; i++) {
        numerator += (values[i] - mean) * (values[i - period] - mean);
        count++;
      }
      
      for (let i = 0; i < n; i++) {
        denominator += Math.pow(values[i] - mean, 2);
      }
      
      if (denominator === 0) continue;
      
      const autocorrelation = numerator / denominator;
      
      // Also calculate cyclic variance ratio
      const groups = [];
      for (let i = 0; i < period && i < values.length; i++) {
        groups[i] = [];
      }
      
      // Group values by position in cycle
      for (let i = 0; i < values.length; i++) {
        const groupIndex = i % period;
        groups[groupIndex].push(values[i]);
      }
      
      // Calculate between-group variance
      const groupMeans = groups.map(g => 
        g.length > 0 ? g.reduce((a, b) => a + b, 0) / g.length : 0
      );
      const grandMean = mean;
      let betweenVar = 0;
      let withinVar = 0;
      
      for (let i = 0; i < groups.length; i++) {
        if (groups[i].length > 0) {
          betweenVar += groups[i].length * Math.pow(groupMeans[i] - grandMean, 2);
          for (const val of groups[i]) {
            withinVar += Math.pow(val - groupMeans[i], 2);
          }
        }
      }
      
      const fRatio = (betweenVar / (period - 1)) / (withinVar / (values.length - period));
      
      console.log(`Period ${period}: autocorrelation = ${autocorrelation.toFixed(3)}, F-ratio = ${fRatio.toFixed(3)}`);
      
      // Lower threshold for business data and consider F-ratio
      // For small datasets, be more lenient with thresholds
      const autocorrelationThreshold = values.length < 12 ? 0.02 : 0.05;
      const fRatioThreshold = values.length < 12 ? 1.2 : 1.5;
      if ((autocorrelation > autocorrelationThreshold || fRatio > fRatioThreshold) && autocorrelation > bestScore) {
        bestScore = autocorrelation;
        bestPeriod = period;
        cyclicScores.set(period, { autocorrelation, fRatio });
      }
    }
    
    // If we found a period with good F-ratio but weak autocorrelation, still use it
    if (bestPeriod === 0) {
      for (const [period, scores] of cyclicScores) {
        if (scores.fRatio > 2.0) {
          bestPeriod = period;
          bestScore = scores.autocorrelation;
          console.log(`Selected period ${period} based on F-ratio ${scores.fRatio.toFixed(3)}`);
          break;
        }
      }
    }
    
    console.log(`Best seasonal period: ${bestPeriod} with score ${bestScore.toFixed(3)}`);
    return bestPeriod;
  }

  /**
   * Detect resolution from configuration or query
   */
  detectResolution() {
    // Check explicit resolution config
    if (this.config.resolution) {
      return;
    }
    
    // Try to detect from predictUntil or other config hints
    const configString = JSON.stringify(this.config).toLowerCase();
    
    if (configString.includes('weekly') || configString.includes('week')) {
      this.config.resolution = 'weekly';
    } else if (configString.includes('monthly') || configString.includes('month')) {
      this.config.resolution = 'monthly';
    } else if (configString.includes('yearly') || configString.includes('year') || configString.includes('annual')) {
      this.config.resolution = 'yearly';
    }
    
    console.log(`ARIMA: Detected resolution: ${this.config.resolution || 'daily'}`);
  }

  /**
   * Lightweight grid search on {p,d,q} ∈ {0-2} using one-step AIC approximation.
   * Runs the first time predict() sees data when any of p/d/q === 'auto'.
   */
  autoSelectPDQ(values) {
    const cand = [0, 1, 2];
    let best = { aic: Infinity, p: 1, d: 1, q: 0 };

    const variance = arr => {
      const m = arr.reduce((a,b)=>a+b,0)/arr.length;
      return arr.reduce((s,v)=>s+Math.pow(v-m,2),0)/arr.length;
    };

    // super-simple differencer
    const diff = (arr, d) => {
      let out = [...arr];
      for (let k=0;k<d;k++) out = out.slice(1).map((v,i)=>v-out[i]);
      return out;
    };

    for (const p of cand) for (const d of cand.slice(0,2)) for (const q of cand) {
      const y = diff(values, d);
      if (y.length < Math.max(p,q)+2) continue;
      // AR variance proxy: var(ε) after p-lag regression
      let err = 0;
      for (let t=p; t<y.length; t++) {
        let yHat = 0;
        for (let i=1;i<=p;i++) yHat += (1/p)*y[t-i];
        err += Math.pow(y[t]-yHat,2);
      }
      const aic = y.length * Math.log(err/y.length) + 2*(p+q+d);
      if (aic < best.aic) best = { aic, p, d, q };
    }
    console.log('ARIMA auto-tune result', best);
    this.p = best.p; this.d = best.d; this.q = best.q;
  }
}

export default ARIMAPredictor;